import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  Users, 
  Clock, 
  Shield, 
  MessageCircle, 
  BarChart3,
  Filter,
  Bell,
  FileText,
  Star,
  Target,
  Activity,
  Eye
} from "lucide-react";

const Features = () => {
  const features = [
    {
      icon: Search,
      title: "Smart Search & Filtering",
      description: "Advanced filtering by MN counties (87 total), number of beds available, accepted age groups (18+ or 55+), genders, care levels, waiver types, available accessibility options, and rental price.",
      category: "Search"
    },
    {
      icon: Bell,
      title: "Smart Notifications",
      description: "Instant notifications when new referrals are submitted to your facility and automatic updates when referral status changes by either party, keeping all stakeholders informed in real-time.",
      category: "Alerts"
    },
    {
      icon: Eye,
      title: "Real-time Click Analytics",
      description: "Track listing engagement with click metrics that reset every 60 days, helping providers optimize visibility and understand which listings generate the most interest.",
      category: "Analytics"
    },
    {
      icon: Star,
      title: "Quality Scorecard Integration",
      description: "View facility quality ratings and safety scores directly from Minnesota's Assisted Living Report Card data, helping placement professionals make informed decisions.",
      category: "Quality"
    },
    {
      icon: MessageCircle,
      title: "Email Workflow Integration",
      description: "Streamlined email-based referral system that opens your default email client with pre-filled context, ensuring professional communication without PHI/PII storage.",
      category: "Communication"
    },
    {
      icon: BarChart3,
      title: "Analytics & Reporting Dashboard",
      description: "Comprehensive dashboard to track placement success rates, referral patterns, and identify trends to improve your placement processes and outcomes.",
      category: "Analytics"
    }
  ];

  return (
    <section id="features" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto">
        <div className="text-center space-y-4 mb-16">
          <Badge variant="secondary" className="text-sm font-medium">
            Platform Features
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            Everything You Need to
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {" "}Streamline Placements
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            CareKonnect provides a comprehensive suite of tools designed specifically 
            for Minnesota's care placement ecosystem.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/20"
              style={{ boxShadow: 'var(--shadow-card)' }}
            >
              <CardHeader className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {feature.category}
                  </Badge>
                </div>
                <CardTitle className="text-xl group-hover:text-primary transition-colors">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">
            Built for Minnesota's Care Community
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            CareKonnect understands the unique needs of Minnesota's healthcare landscape, 
            from rural facilities to urban medical centers.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">87</div>
              <div className="text-sm text-muted-foreground">Counties Served</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent">500+</div>
              <div className="text-sm text-muted-foreground">Active Providers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-success">95%</div>
              <div className="text-sm text-muted-foreground">Faster Placements</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;